import type { Artifact, ToolCall } from '@shared/protocol';
import { parsePublishResult } from '../lib/files';
import { SparkleIcon } from './icons';
import { ToolCard } from './ToolCard';

interface Props {
  tool: ToolCall;
  artifacts: Artifact[];
  onOpen: (id: string, version: number) => void;
}

/**
 * A publish_artifact call, drawn as the thing it produced rather than as a
 * tool row: the title, which version this was, and a way to open it.
 */
export function ArtifactCard({ tool, artifacts, onOpen }: Props) {
  const input = (tool.input ?? {}) as { title?: string; path?: string; note?: string };
  const published = tool.result && !tool.result.isError ? parsePublishResult(tool.result.text) : undefined;

  // A failed publish (a wrong path, say) is worth seeing in full, like any
  // other failed tool call.
  if (tool.result && !published) return <ToolCard tool={tool} />;

  const artifact = published && artifacts.find((a) => a.id === published.id);
  const latest = artifact?.versions.at(-1)?.version;
  const title = artifact?.title ?? input.title ?? input.path ?? 'Artifact';

  let meta: string;
  if (!tool.result) meta = 'Publishing…';
  else if (!artifact) meta = 'Removed from Artifacts';
  else {
    meta = `${artifact.path} · version ${published!.version}`;
    if (latest && latest > published!.version) meta += ` · v${latest} is newer`;
  }

  return (
    <button
      className="artifact-card"
      disabled={!artifact}
      onClick={() => artifact && onOpen(artifact.id, published!.version)}
      title={artifact ? `Open ${title}` : undefined}
    >
      <span className="artifact-card__icon">
        {tool.result ? <SparkleIcon size={16} /> : <span className="spinner" aria-label="publishing" />}
      </span>
      <span className="artifact-card__text">
        <span className="artifact-card__title">{title}</span>
        <span className="artifact-card__meta">{meta}</span>
        {input.note && published && published.version > 1 && (
          <span className="artifact-card__note">{input.note}</span>
        )}
      </span>
      {artifact && <span className="artifact-card__open">Open</span>}
    </button>
  );
}
